import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import quantService from '../../../services/quantService';
import useQuantJob from './hooks/useQuantJob';
import JobStatusBar from './components/JobStatusBar';
import FeatureWeightsTable from './components/FeatureWeightsTable';

const fmt = (v, n = 4) => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(n);

const DiscoveryJobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { job, loading, error, refresh } = useQuantJob('discovery', jobId, 1500);
  const [actionError, setActionError] = useState(null);
  const [actionInfo, setActionInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  if (loading && !job) {
    return <div className="text-sm text-gray-400 py-8 text-center">Loading job…</div>;
  }
  if (!job) {
    return <div className="text-sm text-red-500 py-8 text-center">Job not found.</div>;
  }

  const action = async (label, fn) => {
    setBusy(true);
    setActionError(null);
    setActionInfo(null);
    try {
      const { data } = await fn();
      setActionInfo(`${label}: ${data.status || 'ok'}`);
      refresh();
    } catch (e) {
      setActionError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const canCancel = ['queued', 'running'].includes(job.status);
  const canResume = job.status === 'interrupted_resumable';
  const canStartFresh = ['failed', 'killed', 'cancelled', 'interrupted_resumable'].includes(job.status);
  const canRunAlphalens = job.status === 'done' && !job.alphalens_attempted && job.n_features_out >= 5;
  const canRunBacktest = job.status === 'done' && job.alphalens_passed === true;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/trading/quant')}
            className="text-xs uppercase tracking-wider text-gray-500 hover:text-brand-gold"
          >
            ← Back
          </button>
          <h1 className="text-base font-serif mt-1">
            Discovery <span className="font-mono text-sm text-gray-500">{jobId.slice(0, 8)}…</span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCancel && (
            <button onClick={() => action('Cancelled', () => quantService.cancelDiscovery(jobId))}
                    disabled={busy}
                    className="px-3 py-1 text-xs uppercase tracking-wider border border-gray-300 dark:border-gray-700 rounded hover:border-red-500 hover:text-red-500">
              Cancel
            </button>
          )}
          {canResume && (
            <button onClick={() => action('Resumed', () => quantService.resumeDiscovery(jobId))}
                    disabled={busy}
                    className="px-3 py-1 text-xs uppercase tracking-wider bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded hover:bg-amber-200">
              Resume
            </button>
          )}
          {canStartFresh && (
            <button onClick={() => action('Started fresh', () => quantService.startFreshDiscovery(jobId))}
                    disabled={busy}
                    className="px-3 py-1 text-xs uppercase tracking-wider border border-gray-300 dark:border-gray-700 rounded hover:border-brand-gold hover:text-brand-gold">
              Start Fresh
            </button>
          )}
          {canRunAlphalens && (
            <button onClick={() => {
                      if (!window.confirm('Alphalens runs ONCE per job. Re-running on a tweaked signal invalidates the test. Proceed?')) return;
                      action('Alphalens started', () => quantService.runAlphalens(jobId));
                    }}
                    disabled={busy}
                    className="px-3 py-1 text-xs uppercase tracking-wider bg-brand-gold text-white rounded hover:bg-brand-gold-dark">
              Run Alphalens
            </button>
          )}
          {canRunBacktest && (
            <button onClick={() => navigate(`/trading/quant/backtest?discovery=${jobId}`)}
                    className="px-3 py-1 text-xs uppercase tracking-wider bg-emerald-600 text-white rounded hover:bg-emerald-700">
              Backtest →
            </button>
          )}
        </div>
      </div>

      {(actionError || actionInfo || error) && (
        <div className={`text-xs ${actionError || error ? 'text-red-500' : 'text-emerald-500'}`}>
          {actionError || error || actionInfo}
        </div>
      )}

      {/* Phase progress */}
      <JobStatusBar job={job} type="discovery" />

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Features in" value={job.n_features_in ?? '—'} />
        <Tile label="Survivors" value={job.n_features_out ?? '—'}
              colorize={job.n_features_out != null && job.n_features_out >= 5 ? 'good' : (job.n_features_out != null ? 'bad' : null)} />
        <Tile label="ICIR (top)" value={fmt(job.icir, 3)}
              colorize={(job.icir ?? 0) > 0 ? 'good' : (job.icir ?? 0) < 0 ? 'bad' : null} />
        <Tile label="Resumes" value={job.resume_count ?? 0} />
      </div>

      {/* Surviving features */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Surviving Features
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Features that passed at least 2 of 3 selection methods (IC / ElasticNet / Random Forest).
            Negative weights are contrarian signals, not bugs.
          </p>
        </div>
        <FeatureWeightsTable features={job.surviving_features || []} />
      </div>

      {/* Alphalens results */}
      {job.alphalens_attempted && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
              Alphalens Validation
            </h2>
            <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded ${
              job.alphalens_passed ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20'
              : 'bg-red-50 text-red-500 dark:bg-red-900/20'
            }`}>
              {job.alphalens_passed ? 'PASS' : 'FAIL'}
            </span>
          </div>
          <div className="p-4">
            {job.alphalens_results?.gates && (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left py-1">Gate</th>
                    <th className="text-right py-1">Threshold</th>
                    <th className="text-right py-1">Actual</th>
                    <th className="text-right py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(job.alphalens_results.gates).map(([name, g]) => (
                    <tr key={name} className="border-t border-gray-100 dark:border-gray-800/50">
                      <td className="py-1.5 font-mono text-xs">{name}</td>
                      <td className="py-1.5 text-right text-xs font-mono text-gray-500">{fmt(g.threshold, 3)}</td>
                      <td className={`py-1.5 text-right text-xs font-mono ${g.passed ? 'text-emerald-600' : 'text-red-500'}`}>
                        {fmt(g.actual, 4)}
                      </td>
                      <td className="py-1.5 text-right">
                        {g.passed
                          ? <span className="text-emerald-500">✓</span>
                          : <span className="text-red-500">✗</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Provenance */}
      <div className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
        Universe: {job.universe_name} • Discovery {job.discovery_start} → {job.discovery_end} • Validation {job.validation_start} → {job.validation_end} •
        Label {job.label_type}/{job.label_horizon_days}d • Spot worker: {job.spot_worker_instance_id || '—'}
        {job.survivorship_bias_flag && (
          <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-600">
            survivorship-bias-flag
          </span>
        )}
      </div>
    </div>
  );
};

const Tile = ({ label, value, colorize }) => (
  <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">{label}</div>
    <div className={`text-xl font-serif ${
      colorize === 'good' ? 'text-emerald-600 dark:text-emerald-400' :
      colorize === 'bad' ? 'text-red-500 dark:text-red-400' :
      'text-gray-800 dark:text-gray-200'
    }`}>{value}</div>
  </div>
);

export default DiscoveryJobDetailPage;
